/**
 * Datadog Web Implementation
 *
 * Uses @datadog/browser-rum SDK
 * This file should only be imported by datadog.tsx
 */

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { decamelize, decamelizeKeys } from 'humps';

import type { User } from '#types/user';

import { payingOrgUuid } from './payingOrgUuid';

// Decamelized global-context keys written by initializeUser (Sparta attribute
// names). Centralized so resetUser clears exactly what initializeUser sets —
// these props are persistent and clearUser() does NOT remove them. Keep in sync
// with the keys built in initializeUser below.
const GLOBAL_USER_CONTEXT_KEYS = ['user', 'is_tester', 'is_sso_user'] as const;

/**
 * Initialize user
 */
export const initializeUser = async (user: User): Promise<void> => {
  try {
    const userInfo = {
      id: user.uuid,
      customerProfileUuid: user.customerProfile.uuid,
      payingOrganizationUuid: payingOrgUuid(user),
      // Parity with native (datadog.ts setUserInfo). Surfaces @usr.isTester on web
      // RUM events so prod test accounts can be filtered out of monitoring.
      isTester: user.isTester,
    };
    datadogRum.setUser(userInfo);
    datadogLogs.setUser(userInfo);

    // Mirror Sparta/utilos `setUserContext`: also write the uuid into RUM/Logs
    // global context (`@context.user`), not just the reserved `usr` namespace.
    // `setUser` only populates `@usr.id`, and the session-level `usr` aggregate
    // is NOT backfilled when identify happens mid-session (anonymous account
    // creation → registered) — so sessions surface only `usr.anonymous_id`.
    // Global-context props are stamped on every subsequent event AND surface on
    // the session, which is what Sparta's dashboards/monitors key on. Author
    // camelCase + decamelize at the boundary (user / is_tester / is_sso_user) to
    // match Sparta's attribute names.
    const globalUserContext = decamelizeKeys({
      user: user.uuid,
      isTester: user.isTester,
      isSsoUser: Boolean(user.customerProfile?.ssoPayingOrganization),
    });
    Object.entries(globalUserContext).forEach(([key, value]) => {
      // Match utilos/Sparta (utilos/src/datadog/setGlobalContext.ts): RUM globals
      // are stringified (booleans → "true" / "false") so Sparta-authored monitors
      // faceting on e.g. `is_tester:"true"` match; Logs keeps the raw value, also
      // mirroring utilos.
      datadogRum.setGlobalContextProperty(key, `${value}`);
      datadogLogs.setGlobalContextProperty(key, value);
    });
  } catch (error) {
    console.error('Datadog Web: Failed to initialize user', error);
  }
};

/**
 * Reset user
 */
export const resetUser = async (): Promise<void> => {
  try {
    datadogRum.clearUser();
    datadogLogs.clearUser();

    // clearUser() only clears the reserved usr.* namespace; the global-context
    // props written in initializeUser are persistent and would otherwise stay
    // attached to subsequent logged-out / anonymous events, misattributing them
    // to the previous user.
    GLOBAL_USER_CONTEXT_KEYS.forEach((key) => {
      datadogRum.removeGlobalContextProperty(key);
      datadogLogs.removeGlobalContextProperty(key);
    });
  } catch (error) {
    console.error('Datadog Web: Failed to reset user', error);
  }
};

/**
 * Track error
 */
export const trackError = async (
  error: Error | string,
  context?: Record<string, any>
): Promise<void> => {
  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    datadogRum.addError(errorObj, context);
  } catch (err) {
    console.error('Datadog Web: Failed to track error', err);
  }
};

/**
 * Send a Datadog Logs error entry (logger.error) — distinct from `trackError`'s
 * RUM error. Mirrors Sparta's `logErrorMessage` / `Datadog.sendError`. Use for
 * operational errors monitored / alerted on via Datadog Logs.
 */
export const logError = async (message: string, context?: Record<string, any>): Promise<void> => {
  try {
    datadogLogs.logger.error(message, context);
  } catch (err) {
    console.error('Datadog Web: Failed to log error', err);
  }
};

/**
 * Send a Datadog Logs info entry (logger.info). Mirrors Sparta's
 * `Datadog.sendInfo`. Use for expected, non-fault events worth recording (e.g.
 * an existing-account collision) so they don't inflate error-level alerts.
 */
export const logInfo = async (message: string, context?: Record<string, any>): Promise<void> => {
  try {
    datadogLogs.logger.info(message, context);
  } catch (err) {
    console.error('Datadog Web: Failed to log info', err);
  }
};

/**
 * Track action
 */
export const trackAction = async (name: string, context?: Record<string, any>): Promise<void> => {
  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error('Datadog Web: Failed to track action', error);
  }
};

/**
 * Track view
 */
export const trackView = async (
  _key: string,
  _name: string,
  _attributes?: Record<string, any>
): Promise<void> => {
  // No-op on web: the browser SDK tracks views automatically via URL changes.
  // Attributes are intentionally dropped to avoid leaking state across views
  // (setGlobalContextProperty is persistent and would bleed into subsequent views).
};

/**
 * Set user properties
 */
export const setUserProperties = async (properties: Record<string, any>): Promise<void> => {
  try {
    // Decamelize to Sparta's attribute names (mirrors initializeUser, PostHog's
    // setUserProperties, and utilos/setGlobalContext) so camelCase props collapse
    // onto the same keys instead of duplicating — e.g. `isSsoUser` here merges
    // with the `is_sso_user` written by initializeUser. RUM values are stringified
    // and Logs kept raw, matching utilos, so the two writers can't drift in type.
    Object.entries(decamelizeKeys(properties)).forEach(([key, value]) => {
      datadogRum.setGlobalContextProperty(key, `${value}`);
      datadogLogs.setGlobalContextProperty(key, value);
    });
  } catch (error) {
    console.error('Datadog Web: Failed to set user properties', error);
  }
};

/**
 * Remove user property
 */
export const removeUserProperty = async (key: string): Promise<void> => {
  try {
    // Decamelize so a camelCase key removes the prop stored under its snake_case
    // name by setUserProperties / initializeUser.
    const decamelizedKey = decamelize(key);
    datadogRum.removeGlobalContextProperty(decamelizedKey);
    datadogLogs.removeGlobalContextProperty(decamelizedKey);
  } catch (error) {
    console.error('Datadog Web: Failed to remove user property', error);
  }
};
